'use client'

import { useCallback, useSyncExternalStore, type CSSProperties } from 'react'
import s from './ThemeToggle.module.scss'
import { classnames } from '@/utils/classnames'

const STORAGE_KEY = 'zebra-theme'

function subscribe(onStoreChange: () => void) {
  const handler = () => onStoreChange()
  window.addEventListener('storage', handler)
  window.addEventListener('zebra-theme', handler)
  return () => {
    window.removeEventListener('storage', handler)
    window.removeEventListener('zebra-theme', handler)
  }
}

function getThemeSnapshot(): 'light' | 'dark' {
  if (typeof document === 'undefined') return 'light'
  const a = document.documentElement.getAttribute('data-theme')
  return a === 'dark' ? 'dark' : 'light'
}

function getServerSnapshot(): 'light' | 'dark' {
  return 'light'
}

type ThemeToggleProps = {
  className?: string
  style?: CSSProperties
  /** Wersja pod pasek mobilny (navbar) — stonowane kolory toru i suwaka */
  forMobileNavbar?: boolean
}

export function ThemeToggle({
  className,
  style,
  forMobileNavbar = false,
}: ThemeToggleProps) {
  const resolved = useSyncExternalStore(
    subscribe,
    getThemeSnapshot,
    getServerSnapshot
  )
  const isDark = resolved === 'dark'

  const toggle = useCallback(() => {
    const next = isDark ? 'light' : 'dark'
    document.documentElement.setAttribute('data-theme', next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new Event('zebra-theme'))
  }, [isDark])

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={
        isDark ? 'Przełącz na tryb jasny' : 'Przełącz na tryb ciemny'
      }
      className={classnames(s.root, forMobileNavbar && s.rootMobileNavbar, className)}
      style={style}
      onClick={toggle}
    >
      <span
        className={classnames(s.track, {
          [s.trackDark]: isDark,
          [s.trackLight]: !isDark,
        })}
      >
        <span
          className={classnames(s.iconSlot, s.iconSun, {
            [s.iconSunShow]: !isDark,
          })}
          aria-hidden
        >
          <svg
            className={s.iconSvg}
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
          >
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
          </svg>
        </span>
        <span
          className={classnames(s.thumb, {
            [s.thumbOnDark]: isDark,
            [s.thumbOnLight]: !isDark,
          })}
        />
        <span
          className={classnames(s.iconSlot, s.iconMoon, {
            [s.iconMoonShow]: isDark,
          })}
          aria-hidden
        >
          <svg
            className={s.iconSvg}
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M18.1 15.3a7.5 7.5 0 0 1-9.4-9.4 6.5 6.5 0 1 0 9.4 9.4z" />
            <path
              d="M19 4l.3.8.8.2-.8.2L19 6l-.3-.8-.8-.2.8-.2L19 4zM21 9l.2.5.5.1-.5.1-.2.5-.2-.5-.5-.1.5-.1L21 9z"
              opacity="0.85"
            />
            <path
              d="M16 2l.2.5.5.1-.5.1L16 3l-.2-.5-.5-.1.5-.1L16 2z"
              opacity="0.7"
            />
          </svg>
        </span>
      </span>
    </button>
  )
}
